import axios from "axios";

// En producción, VUE_APP_API_URL viene de .env.production (ver ese archivo);
// en desarrollo local, sin esa variable, cae al backend local de siempre.
const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || "http://localhost:3000/api",
});

// ─── Canal de comunicación entre pestañas ────────────────────────────────────
const channel = new BroadcastChannel("auth_channel");

// ─── Estado local de refresh ──────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

// ─── Lock atómico ─────────────────────────────────────────────────────────────
const LOCK_KEY = "refresh_lock";
const LOCK_TTL = 8000;

function acquireLock() {
  const now = Date.now();
  const raw = localStorage.getItem(LOCK_KEY);

  if (raw) {
    const { ts } = JSON.parse(raw);
    if (now - ts < LOCK_TTL) return null;
  }

  const lockId = `${now}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(LOCK_KEY, JSON.stringify({ ts: now, id: lockId }));

  const written = JSON.parse(localStorage.getItem(LOCK_KEY));
  if (written?.id !== lockId) return null;

  return lockId;
}

function releaseLock(lockId) {
  const raw = localStorage.getItem(LOCK_KEY);
  if (!raw) return;
  const { id } = JSON.parse(raw);
  if (id === lockId) localStorage.removeItem(LOCK_KEY);
}

// ─── Esperar token de otra pestaña ────────────────────────────────────────────
function waitForTokenFromLeader(timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      channel.removeEventListener("message", handler);
      reject(new Error("Timeout esperando token del líder"));
    }, timeoutMs);

    const handler = ({ data }) => {
      if (data.type === "TOKEN_REFRESHED") {
        clearTimeout(timer);
        channel.removeEventListener("message", handler);
        resolve(data.token);
      }
      if (data.type === "REFRESH_FAILED" || data.type === "SESSION_CLOSED") {
        clearTimeout(timer);
        channel.removeEventListener("message", handler);
        reject(new Error(data.type));
      }
    };

    channel.addEventListener("message", handler);
  });
}

// ─── Refresh proactivo ────────────────────────────────────────────────────────
function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

function iniciarRefreshProactivo() {
  setInterval(async () => {
    const token = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");

    if (!token || !refreshToken) return;

    const expiry = getTokenExpiry(token);
    if (!expiry) return;

    const msParaExpirar = expiry - Date.now();

    // Si faltan menos de 2 minutos → refrescar ya
    if (msParaExpirar < 2 * 60 * 1000) {
      const lockId = acquireLock();
      if (!lockId) return; // otra pestaña ya lo está haciendo

      try {
        const { data } = await api.post("/refresh", { refreshToken });
        localStorage.setItem("accessToken", data.accessToken);
        channel.postMessage({ type: "TOKEN_REFRESHED", token: data.accessToken });
      } catch {
        // si falla, el interceptor lo maneja igual
      } finally {
        releaseLock(lockId);
      }
    }
  }, 30 * 1000); // revisar cada 30 segundos
}

iniciarRefreshProactivo();

// ─── Interceptor request ──────────────────────────────────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ─── Interceptor response ─────────────────────────────────────────────────────
api.interceptors.response.use(
  (res) => res,

  async (error) => {
    const original = error.config;

    // 402: la suscripción de la empresa no está activa (subscriptionGate del
    // backend). Se redirige a la pantalla de facturación en vez de dejar
    // que cada vista maneje este caso por su cuenta.
    if (error.response?.status === 402 && window.location.pathname !== "/billing/subscription") {
      window.location.href = "/billing/subscription";
      return Promise.reject(error);
    }

    const skipRefresh =
      original?.url?.endsWith("/login") ||
      original?.url?.endsWith("/refresh");

    if (error.response?.status !== 401 || original._retry || skipRefresh) {
      return Promise.reject(error);
    }

    original._retry = true;

    // ¿Ya está refrescando ESTA pestaña? → encolar
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token) => {
            original.headers.Authorization = `Bearer ${token}`;
            resolve(api(original));
          },
          reject,
        });
      });
    }

    // Intentar adquirir el lock global
    const lockId = acquireLock();

    if (!lockId) {
      // Otra pestaña tiene el lock → esperar su broadcast
      try {
        const token = await waitForTokenFromLeader();
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      } catch {
        return Promise.reject(new Error("No se pudo obtener nuevo token"));
      }
    }

    // Soy el líder: hago el refresh
    isRefreshing = true;

    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) throw new Error("Sin refresh token");

      const { data } = await api.post("/refresh", { refreshToken });
      const newToken = data.accessToken;

      localStorage.setItem("accessToken", newToken);
      channel.postMessage({ type: "TOKEN_REFRESHED", token: newToken });

      processQueue(null, newToken);
      original.headers.Authorization = `Bearer ${newToken}`;
      return api(original);

    } catch (err) {
      processQueue(err);
      channel.postMessage({ type: "REFRESH_FAILED" });

      const status = err.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        channel.postMessage({ type: "SESSION_CLOSED" });
        window.location.href = "/signin";
      }

      return Promise.reject(err);

    } finally {
      isRefreshing = false;
      releaseLock(lockId);
    }
  }
);

// ─── Cierre de sesión desde otra pestaña ─────────────────────────────────────
channel.addEventListener("message", ({ data }) => {
  if (data.type === "SESSION_CLOSED") {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    window.location.href = "/signin";
  }
});

export default api;